import React, { useState } from 'react';
import classnames from 'classnames';
import { toast } from 'react-toastify';
import { getFilePath } from '../../utils';
import { updateTrack, deleteTrack } from '../../pages/title/Audio';
import Input from '../form/Input';
import { ReactComponent as PlayIcon } from '../../svg/play.svg';
import { ReactComponent as PauseIcon } from '../../svg/pause.svg';
import { ReactComponent as DragIcon } from '../../svg/drag.svg';
import { AUDIO_STATUS } from '../../pages/title/AdvancedAudio';

const AudioRow = ({
    track = {},
    readOnly = false,
    group = '',
    onTitleUpdate = () => {},
    onTitleDelete = () => {},
    onPlay = () => {},
    onPause = () => {},
    currentTrack = {},
    isPlaying = false,
    disableTitleEdit = false
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [title, setTitle] = useState(track.name || '');
    const filename = getFilePath(track.filename);
    let isValid = track.status === AUDIO_STATUS.VALID;
    let isPending = track.status === AUDIO_STATUS.PENDING;
    let splitStatus = track?.status?.split(': ');
    let isInvalid = splitStatus?.[0] === AUDIO_STATUS.INVALID;
    let invalidReason = splitStatus?.[1];
    let toastId = React.useRef(null);
    const handleTitleUpdate = async () => {
        if (title === track.name) {
            setIsEditing(false);
            return;
        }

        toastId.current = toast('Saving name...', { autoClose: false });
        try {
            let updateTrackResp = await updateTrack(track.id, { name: title, group });
            toast.update(toastId.current, { render: 'Name Saved', type: toast.TYPE.SUCCESS, autoClose: 3000 });
            onTitleUpdate(group, updateTrackResp.data);
        } catch (e) {
            console.error(e);
            toast.update(toastId.current, { render: 'Error Saving Changes', type: toast.TYPE.ERROR, autoClose: 3000 });
        }

        setIsEditing(false);
    };

    React.useEffect(() => {
        setTitle(track.name);
    }, [track.name]);

    const handleKeyUp = (event) => {
        switch (event.key) {
            case 'Escape':
                setIsEditing(!isEditing);
                break;
            case 'Enter':
                handleTitleUpdate(event);
                break;
            default:
                break;
        }
    };

    const handleDelete = () => {
        deleteTrack(track.id).then(() => onTitleDelete(track.id, group));
    };

    const handlePlay = (event) => {
        event.preventDefault();
        onPlay(track);
    };

    const handlePause = (event) => {
        event.preventDefault();
        onPause(track);
    };

    const isCurrentTrack = currentTrack && currentTrack.id === track.id;

    return (
        <div
            className={classnames('list-group-item chapter-row', {
                readonly: readOnly
            })}
        >
            <div className="chapter-title-wrap">
                {!readOnly && (
                    <div className="chapter-num">
                        <DragIcon />
                    </div>
                )}

                {(readOnly || disableTitleEdit) && (
                    <div className="chapter-title">
                        <span data-rh={track.name || track.filename}>
                            {track.name || filename || 'No Chapter Name'}
                        </span>
                    </div>
                )}

                {!readOnly && !disableTitleEdit && (
                    <div className="chapter-title">
                        {isEditing || !track.name ? (
                            <Input
                                type="text"
                                label="Track Name"
                                placeholder="Give your track a name"
                                hideLabel={true}
                                onBlur={handleTitleUpdate}
                                value={title}
                                onKeyUp={handleKeyUp}
                                onChange={(id, value) => setTitle(value)}
                                required
                                collapse
                            />
                        ) : (
                            <span className="ghost-input" onClick={() => setIsEditing(!isEditing)} data-rh={track.name}>
                                {track.name}
                            </span>
                        )}
                    </div>
                )}
            </div>

            <div className="chapter-file">
                <span data-rh={filename || track.filename}>({filename || 'Invalid File Name'})</span>
            </div>

            <div className="chapter-actions">
                {isPending && <span className="badge badge-warning">Pending Validation</span>}
                {isValid && <span className="badge badge-success">Validated</span>}
                {isInvalid && (
                    <span className="badge badge-error" data-rh={invalidReason}>
                        Error
                    </span>
                )}
                {(!isCurrentTrack || (isCurrentTrack && !isPlaying)) && (
                    <button className="btn btn-sm btn-icon mr-0" data-rh="Play" onClick={handlePlay} type="button">
                        <PlayIcon />
                    </button>
                )}
                {isCurrentTrack && isPlaying && (
                    <button className="btn btn-sm btn-icon mr-0" data-rh="Play" onClick={handlePause} type="button">
                        <PauseIcon />
                    </button>
                )}
                {/* {!readOnly && <button className="btn btn-sm btn-icon mr-0" onClick={handleReplace}><RefreshIcon/></button>} */}
                {!readOnly && (
                    <button className="btn btn-sm btn-link mr-0 txt-danger" onClick={handleDelete} type="button">
                        x
                    </button>
                )}
            </div>
        </div>
    );
};

export default AudioRow;
